/**
 * AddContentFlowType enum
 *
 * Web-only concept: discriminates which publish flow the shared
 * `AddContentSheet` is driving. Flutter has no equivalent enum — it uses
 * distinct screens per entry point (user content, chat media, community,
 * comment, hashtag) rather than a single configurable flow. On web the one
 * stepper-based sheet is reused for every entry point, so the flow type is
 * resolved from the sheet's params and used to trim steps / gate behavior.
 *
 * String-valued (not a Firestore round-trip — it's a pure UI-flow
 * discriminator), so member order is inert. Member order matches the task
 * prompt — `user`, `chat`, `community`, `comment`, `hashtag`, `feedback` —
 * with `profileImage` appended after.
 *
 * `feedback` drives an **image-only** two-step (Drop→Preview) collect-not-send
 * variant of the sheet (Tasks 5-6): Preview "Done" collects the local image
 * into the feedback page's draft list rather than uploading/sending it.
 *
 * `profileImage` drives the same **image-only** two-step (Drop→Preview)
 * collect-not-send shape as `feedback`, its sibling: Preview "Done" hands the
 * picked **local** image back to `EditUserProfilePage` for the avatar preview
 * rather than uploading it. The upload happens later, from that page's own
 * "Done" save path — never at pick time. Ports Flutter's
 * `edit_user_profile_screen.dart:270-286` (`onChangeImageTapped`), whose
 * picker call is image-only (`allowVideo: false`, `allowAudio: false`) and
 * local-returning (`returnLocalMedia: true`, `uploadOnly: true`).
 *
 * `comment` drives the **full five-step user flow** for a video reply published
 * from the content comments sheet — NOT a trimmed message flow. It deliberately
 * inherits every `user`-flow default: video-only drop (`resolveDropStepConfig`
 * falls through to `{}`, the web enforcement of Flutter's `allowImage: false`,
 * `comments_bottom_sheet.dart:919`), the `CONTENT_MIN/MAX_SECONDS` duration band
 * (`isMessageVideoFlow` stays false) and all five steps (`isMessageFlow` stays
 * false) — matching Flutter, whose comment reply runs the ordinary publish chain
 * (`add_content_screen.dart:678-706`). Two things differ: the picker step's
 * title is "Reply comment" (`comments_bottom_sheet.dart:916`, forwarded to the
 * app bar at `add_content_screen.dart:1057-1062`; steps 2-5 keep their own
 * titles), and the Content Settings step drops its Schedule row (Flutter renders
 * an empty `Container()` when `contentReplyCommentInfo != null`,
 * `content_settings_screen.dart:565-568`).
 *
 * `hashtag` is the only member still reserved for a future flow; every other
 * member is wired.
 */
export enum AddContentFlowType {
  user = 'user',
  chat = 'chat',
  community = 'community',
  comment = 'comment',
  hashtag = 'hashtag',
  feedback = 'feedback',
  profileImage = 'profileImage',
}
